import axios from 'axios';
import { auth } from '../firebase';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '/api',
  // Per-request timeout. 12s is enough for normal mobile networks while
  // failing fast on truly broken connections (the retry interceptor below
  // will give us 3 total attempts = up to 36s before bubbling the error).
  timeout: 12000,
  // Do NOT set a default Content-Type here.
  // • For plain-object POST/PUT bodies Axios's transformRequest handles it and
  //   sets 'application/json' automatically.
  // • For FormData uploads the browser must set 'multipart/form-data; boundary=...'
  //   itself — any explicit Content-Type we provide here would omit the boundary
  //   parameter and break multer file parsing on the server.
});

// Attach a fresh Firebase ID token on every request.
// Firebase ID tokens expire every hour; `getIdToken()` transparently refreshes
// them when needed. Storing a long-lived token in localStorage (the old
// approach) was an XSS risk — by always pulling from the SDK we never expose
// the token to JavaScript outside the request flow.
api.interceptors.request.use(
  async (config) => {
    try {
      const user = auth.currentUser;
      if (user) {
        const token = await user.getIdToken();
        config.headers.Authorization = `Bearer ${token}`;
      }
    } catch (err) {
      // If token retrieval fails the request will go out unauthenticated and
      // the backend will respond 401 — which the response interceptor below
      // turns into a redirect to /login.
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Retry interceptor — handles flaky mobile networks gracefully.
//
// We retry safe idempotent requests (GET, HEAD, OPTIONS) when the failure
// looks transient: network error, timeout, or 5xx from the server. Up to 2
// retries with exponential backoff (400ms, 1200ms). This turns the typical
// 3G "single dropped packet → user sees error" pattern into a silent recovery.
//
// We deliberately do NOT retry POST/PUT/PATCH/DELETE — those may not be
// idempotent (e.g. placing an order, charging a payment) and a retry could
// cause double-billing or duplicate writes.
const RETRY_DELAYS_MS = [400, 1200]; // attempts 2 and 3
const isRetryableError = (error) => {
  // No response = network error or timeout
  if (!error.response) return true;
  // 5xx server errors are usually transient
  if (error.response.status >= 500 && error.response.status < 600) return true;
  // 408 Request Timeout, 429 Too Many Requests
  if (error.response.status === 408 || error.response.status === 429) return true;
  return false;
};
const isIdempotent = (config) => {
  const method = (config?.method || 'get').toLowerCase();
  return method === 'get' || method === 'head' || method === 'options';
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const config = error.config;
    // Try the retry path first — falls through to the 401/redirect logic below
    // if we've exhausted retries or the request isn't safe to retry.
    if (config && isIdempotent(config) && isRetryableError(error)) {
      config.__retryCount = config.__retryCount || 0;
      if (config.__retryCount < RETRY_DELAYS_MS.length) {
        const delay = RETRY_DELAYS_MS[config.__retryCount];
        config.__retryCount += 1;
        if (process.env.NODE_ENV === 'development') {
          console.warn(`[api retry] attempt ${config.__retryCount} for ${config.method?.toUpperCase()} ${config.url} in ${delay}ms`);
        }
        await new Promise((r) => setTimeout(r, delay));
        return api(config);
      }
    }
    if (error.response?.status === 401) {
      const url = error.config?.url || '';
      // Don't redirect if the call itself is part of the auth flow.
      const isAuthAttempt =
        url.includes('/auth/firebase-sync') || url.includes('/auth/me');
      if (!isAuthAttempt) {
        try {
          await auth.signOut();
        } catch (_) { /* ignore */ }
        if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
      }
    }
    return Promise.reject(error);
  }
);

export default api;
